import React, { useEffect, useRef, useState } from "react";
import SimpleBarReact from "simplebar-react";
import BraineeQuestionCard from "./assessment/BraineeQuestionCard";
import { QuestionCircleOutlined } from "@ant-design/icons";

/**
 * ScrollViewQuestions component
 * Props:
 * - answerSheetData
 * - answerData
 * - handleAnswerRequest
 * - questionRefs
 * - idxCol
 * - sectionIdx
 * - qcardIdx
 * - location
 */
import { Row, Col, Space, Select, Popconfirm, Button, Typography } from "antd";

const ScrollViewQuestions = ({
   answerSheetData,
   answerData,
   handleAnswerRequest,
   questionRefs,
   idxCol,
   sectionIdx,
   qcardIdx,
   location,
   questionIndex,
   handleGoTo,
   onQuestionIndexChange,
   isSubmitted,
   handleSubmit,
   hasUnansweredQuestions,
   Text,
   assessment_type,
   onTrustedUploadStart,
   onTrustedUploadEnd,
   mobileVirtualKeyboard,
}) => {
   const scrollContainerRef = useRef(null);
   const lastReportedQuestionIndexRef = useRef(questionIndex);

   // Enable submit once the user reaches the last rendered card, without
   // forcing them to scroll through any intentional spacer below it.
   const [isLastElementVisible, setIsLastElementVisible] = useState(false);

   useEffect(() => {
      lastReportedQuestionIndexRef.current = questionIndex;
   }, [questionIndex]);

   useEffect(() => {
      const hostNode = scrollContainerRef.current;
      const scrollNode = hostNode?.querySelector(".simplebar-content-wrapper");
      const contentNode = hostNode?.querySelector(".simplebar-content");

      if (!scrollNode) return undefined;

      const getQuestionElements = () => [...(questionRefs.current || [])].filter(Boolean);
      const getLastQuestionElement = () => getQuestionElements().at(-1) || null;

      const updateCurrentQuestion = () => {
         if (typeof onQuestionIndexChange !== "function") return;

         const questionElements = getQuestionElements();
         if (questionElements.length === 0) return;

         const viewportMiddle = scrollNode.scrollTop + (scrollNode.clientHeight / 2);
         let activeQuestionIndex = 1;
         let closestDistance = Number.POSITIVE_INFINITY;

         questionElements.forEach((element, idx) => {
            const elementTop = element.offsetTop;
            const elementBottom = elementTop + element.offsetHeight;
            const elementMiddle = elementTop + (element.offsetHeight / 2);

            if (viewportMiddle >= elementTop && viewportMiddle <= elementBottom) {
               activeQuestionIndex = idx + 1;
               closestDistance = -1;
               return;
            }

            if (closestDistance === -1) return;

            const distance = Math.abs(elementMiddle - viewportMiddle);
            if (distance < closestDistance) {
               closestDistance = distance;
               activeQuestionIndex = idx + 1;
            }
         });

         if (lastReportedQuestionIndexRef.current !== activeQuestionIndex) {
            lastReportedQuestionIndexRef.current = activeQuestionIndex;
            onQuestionIndexChange(activeQuestionIndex);
         }
      };

      const updateLastElementVisibility = () => {
         const lastQuestionElement = getLastQuestionElement();

         if (!lastQuestionElement) {
            setIsLastElementVisible(false);
            return;
         }

         const viewportBottom = scrollNode.scrollTop + scrollNode.clientHeight;
         const lastQuestionTop = lastQuestionElement.offsetTop;
         const lastQuestionBottom = lastQuestionTop + lastQuestionElement.offsetHeight;
         const tolerance = Math.max(16, Math.round(scrollNode.clientHeight * 0.02));

         const hasReachedLastQuestion = viewportBottom >= lastQuestionTop + tolerance;
         const hasReachedLastQuestionBottom = viewportBottom >= lastQuestionBottom - tolerance;

         setIsLastElementVisible(hasReachedLastQuestion || hasReachedLastQuestionBottom);
      };

      const handleScrollStateChange = () => {
         updateCurrentQuestion();
         updateLastElementVisibility();
      };

      handleScrollStateChange();
      scrollNode.addEventListener("scroll", handleScrollStateChange, { passive: true });
      window.addEventListener("resize", handleScrollStateChange);

      let resizeObserver;
      if (typeof ResizeObserver !== "undefined") {
         resizeObserver = new ResizeObserver(handleScrollStateChange);
         resizeObserver.observe(scrollNode);
         if (contentNode) {
            resizeObserver.observe(contentNode);
         }

         getQuestionElements().forEach((element) => {
            resizeObserver.observe(element);
         });
      }

      return () => {
         scrollNode.removeEventListener("scroll", handleScrollStateChange);
         window.removeEventListener("resize", handleScrollStateChange);
         resizeObserver?.disconnect();
      };
   }, [answerSheetData, onQuestionIndexChange, questionRefs]);

   let localSectionIdx = sectionIdx;
   let localQcardIdx = qcardIdx;
   let localIdxCol = [...idxCol];

   let optionSectionIdx = sectionIdx;
   let optionQcardIdx = qcardIdx;

   return (
      <div
         ref={scrollContainerRef}
         className="answer-sheet-content3"
         style={{ display: "flex", flexDirection: "column", flex: "1 1 auto", minHeight: 0 }}
      >
         <SimpleBarReact style={{ flex: "1 1 auto", minHeight: 0, paddingBottom: 16 }}>
            <ul className="questions">
               {answerSheetData.map(
                  (
                     {
                        id,
                        type,
                        points,
                        option_labels,
                        question,
                        image_url,
                        template,
                        question_image,
                        question_audio,
                        question_video,
                     },
                     idx
                  ) => {
                     if (type === "section") {
                        localQcardIdx = 1;
                        localIdxCol.push(localSectionIdx);
                        localSectionIdx++;
                     } else {
                        localIdxCol.push(localQcardIdx);
                        localQcardIdx++;
                     }

                     return (
                        <li ref={(el) => (questionRefs.current[idx] = el)} key={id || idx}>
                           <div className="question-center-container">
                              <BraineeQuestionCard
                                 index={localIdxCol[idx]}
                                 id={idx}
                                 type={type}
                                 points={points}
                                 correct={answerData[idx].answer}
                                 option_labels={option_labels}
                                 question={question}
                                 image_url={image_url}
                                 question_type={"2"}
                                 mode={"answer"}
                                 answerCallback={handleAnswerRequest}
                                 template={template}
                                 question_image={question_image}
                                 question_audio={question_audio}
                                 question_video={question_video}
                                 subject_name={location.state.subject}
                                 assessment_type={assessment_type}
                                 onTrustedUploadStart={onTrustedUploadStart}
                                 onTrustedUploadEnd={onTrustedUploadEnd}
                                 mobileVirtualKeyboard={mobileVirtualKeyboard}
                              />
                           </div>
                        </li>
                     );
                  }
               )}
            </ul>
         </SimpleBarReact>
         {/* Navigation section */}
         <div className="assessment-question-navigation">
            <Row gutter={12} className="assessment-question-navigation-row">
               <Col span={24} style={{ display: "flex", justifyContent: "center" }}>
                  <Space>
                     <div>
                        <Select
                           size="default"
                           style={{ width: '70px', textAlign: "center", borderRadius: "7px" }}
                           value={questionIndex}
                           onChange={handleGoTo}
                        >
                           {
                              answerSheetData.map(({id, type}, idx) => {
                                 if (type === "section") {
                                    optionQcardIdx = 1;
                                    optionSectionIdx++;
                                 } else {
                                    optionQcardIdx++;
                                 }

                                 return (
                                    <Select.Option key={idx + 1} value={idx + 1}>
                                       {
                                          type === "section" ? `S${optionSectionIdx-1}` : optionQcardIdx-1
                                       }
                                    </Select.Option>
                                 );
                              })
                           }
                        </Select>
                     </div>
                     <Popconfirm
                        title="Click 'Yes', if you have already completed your answers, 'No', if you want to continue answering."
                        onConfirm={handleSubmit}
                        okText="Yes"
                        cancelText="No"
                        icon={<QuestionCircleOutlined />}
                        disabled={ (typeof isSubmitted !== "undefined" ? isSubmitted : false) || !isLastElementVisible }
                     >
                        <Button
                           className='button-shadow'
                           type='primary'
                           block shape='round'
                           disabled={ (typeof isSubmitted !== "undefined" ? isSubmitted : false) || !isLastElementVisible }
                        >
                           <Typography.Text ellipsis style={{ color: "#fff" }}>Submit Answer</Typography.Text>
                        </Button>
                     </Popconfirm>
                  </Space>
               </Col>
            </Row>
         </div>
      </div>
   );
};

export default ScrollViewQuestions;
